import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult, useQueryClient } from 'react-query';

import { useNavigationData } from 'hooks/useNavigationData';
import { useSPMPlanContext } from 'contexts/SPMPlanContext';
import { MAX_PREFETCH_ATTEMPTS, useSPMPrefetchTokenRegistry } from 'contexts/SPMPrefetchTokenContext';
import { useTQPrefetchTokenRegistry } from 'contexts/TQPrefetchTokenContext';
import { useDebouncedToast } from 'hooks/useDebouncedToast';
import { usePrefetchMutation } from 'services/SPM/hooks/usePrefetchMutation';
import { SPMPrefetchRecordsParams, SPMPrefetchRecordsResponse } from 'types/SPM/spmRecords';
import { APIError } from 'utils/errors';
import { useApprovalContext } from 'contexts/ApprovalContext';

type UsePrefetchRecordsReturn = {
  prefetchMutation: UseMutationResult<SPMPrefetchRecordsResponse, APIError, SPMPrefetchRecordsParams, unknown>;
};

/**
 * Hook to prefetch SPM records and manage prefetch tokens.
 */
export const usePrefetchRecords = (isPrefetchActive = true): UsePrefetchRecordsReturn => {
  const { activeScenario } = useNavigationData();
  const queryClient = useQueryClient();
  const { showDebouncedErrorToast, clearDebounceKey } = useDebouncedToast();
  const { clearAllTokens: clearAllTQTokens } = useTQPrefetchTokenRegistry();
  const { getToken, registerToken, getTokenKey, getPrefetchAttempts, incrementPrefetchAttempts, clearRegistry } =
    useSPMPrefetchTokenRegistry();
  const [isFetchingNewToken, setIsFetchingNewToken] = useState(false);
  const { planningCycle, planMetricsWorksheet } = useSPMPlanContext();
  const { proposedChangeGroupId, activeChangeGroupId, isApprovalMode } = useApprovalContext();
  const { t } = useTranslation();

  // Generate the token key for this view and worksheet/model
  const tokenKey = getTokenKey();
  const existingTokenInRegistry = getToken();

  /**
   * Checks if any records queries for the current planning cycle are loading for the first time.
   * This prevents clearing prefetch tokens when the page is still in its initial load state.
   */
  const checkIsCurrentPageLoadingForFirstTime = useCallback(() => {
    if (!planningCycle) {
      return false;
    }

    const recordsQueries = queryClient.getQueryCache().findAll({
      predicate: (query) => {
        const key = query.queryKey as string[];
        // Check if it's a records query for this planning cycle
        return (
          Array.isArray(key) &&
          key[0] === 'spm' &&
          key[1] === 'planning-cycles' &&
          key[2] === planningCycle.id &&
          key.includes('records')
        );
      },
    });

    return recordsQueries.some((query) => query.state.status === 'loading' && !query.state.data);
  }, [queryClient, planningCycle]);

  const { mutation: prefetchMutation } = usePrefetchMutation({
    onPrefetchStart: () => {
      // Set loading state when prefetch starts
      setIsFetchingNewToken(true);
    },
    onPrefetchComplete: (token) => {
      // Register token in the registry once prefetch completes
      // Uses active scenario (no scenarioId passed)
      if (tokenKey) {
        registerToken(token);
      }
    },
    onPrefetchFinally: () => {
      // Clear loading state
      setIsFetchingNewToken(false);
    },
    onMutationError: () => {
      // Clear loading state on mutation error
      setIsFetchingNewToken(false);
    },
    getErrorToastKey: (errorMessage) => `${errorMessage}-${tokenKey}`,
  });

  // Trigger prefetch when token doesn't exist and max retries not exceeded
  useEffect(() => {
    // Skip if preconditions not met

    if (
      !tokenKey ||
      existingTokenInRegistry ||
      prefetchMutation.isLoading ||
      isFetchingNewToken ||
      !planningCycle ||
      !planMetricsWorksheet ||
      !isPrefetchActive ||
      (isApprovalMode && !proposedChangeGroupId)
    ) {
      return;
    }

    const attempts = getPrefetchAttempts();

    // Check retry limit
    // TODO: stop attempting to retry if the snapshot log is not found. We dont have a good way of passing around
    // whether the snapshot log is found or not.
    if (attempts >= MAX_PREFETCH_ATTEMPTS) {
      return;
    }

    // Increment counter before attempting prefetch
    incrementPrefetchAttempts();
    prefetchMutation.mutate({
      planningCycleId: planningCycle.id,
      spmModelId: undefined, // endpoint doesn't need model since we grab all columns by default
      worksheetId: planMetricsWorksheet.id,
      scenarioId: activeScenario.id,
      proposedChangeGroupId: activeChangeGroupId,
    });
  }, [
    tokenKey,
    existingTokenInRegistry,
    prefetchMutation.isLoading,
    isFetchingNewToken,
    planningCycle,
    planMetricsWorksheet,
    activeScenario.id,
    activeChangeGroupId,
    isApprovalMode,
    proposedChangeGroupId,
    getPrefetchAttempts,
    incrementPrefetchAttempts,
    showDebouncedErrorToast,
    clearDebounceKey,
    t,
    prefetchMutation,
    isPrefetchActive,
  ]);

  // Clear token when queries are invalidated (e.g., after mutations)
  useEffect(() => {
    if (!planningCycle || !tokenKey || !isPrefetchActive) {
      return undefined;
    }

    const queryCache = queryClient.getQueryCache();

    return queryCache.subscribe((event) => {
      if (!event || event.type !== 'queryUpdated' || !event.query.state.isInvalidated) {
        return;
      }
      const queryKey = event.query.queryKey as string[];

      if (!Array.isArray(queryKey) || queryKey.length < 3) {
        return;
      }

      // Only clear token for invalidations that affect ALL worksheets/records:
      const isSPMQuery = queryKey[0] === 'spm';
      const isPlanningCycleQuery = queryKey[1] === 'planning-cycles';
      const matchesPlanningCycle = queryKey[2] === planningCycle.id;

      if (!isSPMQuery || !isPlanningCycleQuery || !matchesPlanningCycle) {
        return;
      }

      // Top level invalidation ['spm', 'planning-cycles', planningCycleId]
      if (queryKey.length === 3) {
        clearAllTQTokens();
        clearRegistry();
        clearDebounceKey(tokenKey);
        return;
      }

      // If the query ends with 'worksheets' we know it's a top level invalidation for all worksheets
      // This handles planning cycle and scenario level worksheets invalidations.
      const lastElement = queryKey[queryKey.length - 1];

      // Check if page is still in initial load state before clearing tokens
      const isCurrentPageLoadingForTheFirstTime = checkIsCurrentPageLoadingForFirstTime();

      if (lastElement === 'worksheets' && !isCurrentPageLoadingForTheFirstTime) {
        // TODO: do some checks before clearing tq sheets. not all users would need this.
        clearAllTQTokens();
        clearRegistry();
        clearDebounceKey(tokenKey);
      }
    });
  }, [
    planningCycle,
    queryClient,
    tokenKey,
    clearRegistry,
    clearDebounceKey,
    clearAllTQTokens,
    isPrefetchActive,
    checkIsCurrentPageLoadingForFirstTime,
  ]);

  // Clear token when proposed change group or toggle state has changed
  useEffect(() => {
    if (!tokenKey || !isPrefetchActive) {
      return;
    }

    clearAllTQTokens();
    clearRegistry();
    clearDebounceKey(tokenKey);
  }, [activeChangeGroupId, tokenKey, clearRegistry, clearDebounceKey, clearAllTQTokens, isPrefetchActive]);

  return {
    prefetchMutation,
  };
};
